import { call, fork, put, takeEvery } from 'redux-saga/effects';
import {
  GET_INITIAL_DASHBOARD_DATA,
  GET_CRS_DASHBOARD_SEGMENT_LIST_DATA,
  GET_CRS_MODEL_LIST_DATA,
  GET_CRS_CLUSTER_CHART_DATA,
  GET_CRS_CLUSTER_FEATURES_DATA,
  GET_CRS_CLUSTER_CUSTOMER_LIST_DATA,
  GET_CRS_FEATURE_CATEGORY_CODES,
  RESET_CRS_DASHBOARD,
  getCrsSegmentListDataSuccess,
  getCrsSegmentListDataFail,
  getCrsModelListDataSuccess,
  getCrsModelListDataFail,
  getCrsClusterChartDataSuccess,
  getCrsClusterChartDataFail,
  getCrsClusterFeaturesDataSuccess,
  getCrsClusterFeaturesDataFail,
  getCrsClusterCustomerListDataSuccess,
  getCrsClusterCustomerListDataFail,
  getCrsClusterCategoryCodesSuccess,
  getCrsClusterCategoryCodesFail
} from '../../redux/actions/crs/crsDashboard';
import * as dashboardApis from '../../services/crs/crsDashboard';
import * as listViewApis from '../../services/crs/crsListView';
import * as riskDetailApis from '../../services/crs/crsRiskDetail';

const listViewNetworkLayerObject = new listViewApis.NetworkLayer();
const riskDetailNetworkLayerObject = new riskDetailApis.NetworkLayer();
const dashboardNetworkLayerObject = new dashboardApis.NetworkLayer();

const CLUSTER_DATA_KEYS = {
  xKey: 'severityScore',
  yKey: 'yieldRate',
  zKey: 'clusterSize'
};

const getClusterQuadrant = (cluster, convertToPercentage, xKey, yKey) => {
  if (cluster) {
    let x, y;

    if (xKey) {
      x = cluster[xKey];
    } else {
      x = cluster.x;
    }

    if (yKey) {
      y = cluster[yKey];
    } else {
      y = cluster.y;
    }

    // Q1
    if (
      x * (convertToPercentage ? 100 : 1) >= 50 &&
      y * (convertToPercentage ? 100 : 1) >= 50
    ) {
      return 1;
    }

    // Q2
    if (
      x * (convertToPercentage ? 100 : 1) <= 50 &&
      y * (convertToPercentage ? 100 : 1) >= 50
    ) {
      return 2;
    }

    // Q3
    if (
      x * (convertToPercentage ? 100 : 1) <= 50 &&
      y * (convertToPercentage ? 100 : 1) <= 50
    ) {
      return 3;
    }

    // Q4
    if (
      x * (convertToPercentage ? 100 : 1) >= 50 &&
      y * (convertToPercentage ? 100 : 1) <= 50
    ) {
      return 4;
    }
  }

  return null;
};

const getDefaultCluster = (clusterData) => {
  let defaultCluster;
  if (Array.isArray(clusterData) && clusterData.length > 0) {
    // loop and see which cluster is in the 1st quadrant
    clusterData.forEach((clusterItem) => {
      // if cluster is in first quadrant
      if (
        getClusterQuadrant(
          clusterItem,
          true,
          CLUSTER_DATA_KEYS.xKey,
          CLUSTER_DATA_KEYS.yKey
        ) === 1
      ) {
        // if no cluster is assigned as default yet
        if (!defaultCluster) {
          defaultCluster = clusterItem;
        } else {
          // check if current default cluster is smaller than current item
          if (clusterItem.clusterSize > defaultCluster.clusterSize) {
            defaultCluster = clusterItem;
          }
        }
      }
    });

    if (defaultCluster) {
      return defaultCluster;
    }

    // loop and see which cluster is in the 1st quadrant
    clusterData.forEach((clusterItem) => {
      // if cluster is in first quadrant
      if (
        getClusterQuadrant(
          clusterItem,
          true,
          CLUSTER_DATA_KEYS.xKey,
          CLUSTER_DATA_KEYS.yKey
        ) === 2
      ) {
        // if no cluster is assigned as default yet
        if (!defaultCluster) {
          defaultCluster = clusterItem;
        } else {
          // check if current default cluster is smaller than current item
          if (clusterItem.clusterSize > defaultCluster.clusterSize) {
            defaultCluster = clusterItem;
          }
        }
      }
    });

    if (defaultCluster) {
      return defaultCluster;
    }

    // loop and see which cluster is in the 1st quadrant
    clusterData.forEach((clusterItem) => {
      // if cluster is in first quadrant
      if (
        getClusterQuadrant(
          clusterItem,
          true,
          CLUSTER_DATA_KEYS.xKey,
          CLUSTER_DATA_KEYS.yKey
        ) === 3
      ) {
        // if no cluster is assigned as default yet
        if (!defaultCluster) {
          defaultCluster = clusterItem;
        } else {
          // check if current default cluster is smaller than current item
          if (clusterItem.clusterSize > defaultCluster.clusterSize) {
            defaultCluster = clusterItem;
          }
        }
      }
    });

    if (defaultCluster) {
      return defaultCluster;
    }

    // loop and see which cluster is in the 1st quadrant
    clusterData.forEach((clusterItem) => {
      // if cluster is in first quadrant
      if (
        getClusterQuadrant(
          clusterItem,
          true,
          CLUSTER_DATA_KEYS.xKey,
          CLUSTER_DATA_KEYS.yKey
        ) === 4
      ) {
        // if no cluster is assigned as default yet
        if (!defaultCluster) {
          defaultCluster = clusterItem;
        } else {
          // check if current default cluster is smaller than current item
          if (clusterItem.clusterSize > defaultCluster.clusterSize) {
            defaultCluster = clusterItem;
          }
        }
      }
    });

    if (defaultCluster) {
      return defaultCluster;
    }
  }

  return null;
};

function* getSegmentListDataWorker(payload) {
  try {
    const response = yield call(
      listViewNetworkLayerObject.segmentListAPI,
      payload
    );
    yield put(getCrsSegmentListDataSuccess(response));
  } catch (error) {
    yield put(getCrsSegmentListDataFail({ error: '' }));
  }
}

export function* watchGetSegmentListData() {
  yield takeEvery(GET_CRS_DASHBOARD_SEGMENT_LIST_DATA, function* ({ payload }) {
    yield fork(getSegmentListDataWorker, payload);
  });
}

function* getModelListDataWorker(payload) {
  try {
    const response = yield call(
      dashboardNetworkLayerObject.modelListAPI,
      payload
    );
    yield put(getCrsModelListDataSuccess(response));
  } catch (error) {
    yield put(getCrsModelListDataFail({ error: '' }));
  }
}

export function* watchGetModelListData() {
  yield takeEvery(GET_CRS_MODEL_LIST_DATA, function* ({ payload }) {
    yield fork(getModelListDataWorker, payload);
  });
}

function* getInitialDashboardDataWorker(payload) {
  try {
    const response = yield call(
      dashboardNetworkLayerObject.modelListAPI,
      payload
    );
    yield put(getCrsModelListDataSuccess(response));

    let clusterList = null;

    if (Array.isArray(response) && response.length > 0) {
      const clusterChartPayload = {
        modelId: response[0].value
      };
      const clusterChartResponse = yield call(
        dashboardNetworkLayerObject.clusterChartAPI,
        clusterChartPayload
      );
      yield put(getCrsClusterChartDataSuccess(clusterChartResponse));

      clusterList = clusterChartResponse;
    }

    if (Array.isArray(clusterList) && clusterList.length > 0) {
      const defaultCluster = getDefaultCluster(clusterList);

      if (defaultCluster) {
        const clusterRelatedPayload = {
          clusterId: defaultCluster.clusterId
        };

        const clusterFeaturesResponse = yield call(
          dashboardNetworkLayerObject.clusterFeaturesAPI,
          clusterRelatedPayload
        );
        yield put(getCrsClusterFeaturesDataSuccess(clusterFeaturesResponse));

        const customerListResponse = yield call(
          listViewNetworkLayerObject.customerListAPI,
          clusterRelatedPayload
        );
        yield put(getCrsClusterCustomerListDataSuccess(customerListResponse));
      }
    }
  } catch (error) {
    yield put(getCrsModelListDataFail({ error: '' }));
  }
}

function* resetDashboardDataWorker(payload) {
  try {
    const response = yield call(
      dashboardNetworkLayerObject.modelListAPI,
      payload
    );
    yield put(getCrsModelListDataSuccess(response));

    let clusterList = null;

    if (Array.isArray(response) && response.length > 0) {
      const clusterChartPayload = {
        modelId: response[0].value
      };
      const clusterChartResponse = yield call(
        dashboardNetworkLayerObject.clusterChartAPI,
        clusterChartPayload
      );
      yield put(getCrsClusterChartDataSuccess(clusterChartResponse));

      clusterList = clusterChartResponse;
    }

    if (Array.isArray(clusterList) && clusterList.length > 0) {
      const defaultCluster = getDefaultCluster(clusterList);

      if (defaultCluster) {
        const clusterRelatedPayload = {
          clusterId: defaultCluster.clusterId
        };

        const clusterFeaturesResponse = yield call(
          dashboardNetworkLayerObject.clusterFeaturesAPI,
          clusterRelatedPayload
        );
        yield put(getCrsClusterFeaturesDataSuccess(clusterFeaturesResponse));

        const customerListResponse = yield call(
          listViewNetworkLayerObject.customerListAPI,
          clusterRelatedPayload
        );
        yield put(getCrsClusterCustomerListDataSuccess(customerListResponse));
      }
    }
  } catch (error) {
    yield put(getCrsModelListDataFail({ error: '' }));
  }
}

export function* watchCrsGetInitialDashboardData() {
  yield takeEvery(GET_INITIAL_DASHBOARD_DATA, function* ({ payload }) {
    yield fork(getInitialDashboardDataWorker, payload);
  });
}

export function* watchCrsResetDashboardData() {
  yield takeEvery(RESET_CRS_DASHBOARD, function* ({ payload }) {
    yield fork(resetDashboardDataWorker, payload);
  });
}

function* getClusterChartDataWorker(payload) {
  try {
    const response = yield call(
      dashboardNetworkLayerObject.clusterChartAPI,
      payload
    );
    yield put(getCrsClusterChartDataSuccess(response));
  } catch (error) {
    yield put(getCrsClusterChartDataFail({ error: '' }));
  }
}

export function* watchGetClusterChartData() {
  yield takeEvery(GET_CRS_CLUSTER_CHART_DATA, function* ({ payload }) {
    yield fork(getClusterChartDataWorker, payload);
  });
}

function* getClusterFeaturesDataWorker(payload) {
  try {
    const response = yield call(
      dashboardNetworkLayerObject.clusterFeaturesAPI,
      payload
    );
    yield put(getCrsClusterFeaturesDataSuccess(response));
  } catch (error) {
    yield put(getCrsClusterFeaturesDataFail({ error: '' }));
  }
}

export function* watchGetClusterFeaturesData() {
  yield takeEvery(GET_CRS_CLUSTER_FEATURES_DATA, function* ({ payload }) {
    yield fork(getClusterFeaturesDataWorker, payload);
  });
}

function* getClusterCustomerListDataWorker(payload) {
  try {
    const response = yield call(
      listViewNetworkLayerObject.customerListAPI,
      payload
    );
    yield put(getCrsClusterCustomerListDataSuccess(response));
  } catch (error) {
    yield put(getCrsClusterCustomerListDataFail({ error: '' }));
  }
}

export function* watchGetClusterCustomerListData() {
  yield takeEvery(GET_CRS_CLUSTER_CUSTOMER_LIST_DATA, function* ({ payload }) {
    yield fork(getClusterCustomerListDataWorker, payload);
  });
}

function* getFeatureCategoryCodesDataWorker(payload) {
  try {
    const response = yield call(
      riskDetailNetworkLayerObject.featureCategoryCodesAPI,
      payload
    );
    yield put(getCrsClusterCategoryCodesSuccess(response));
  } catch (error) {
    yield put(getCrsClusterCategoryCodesFail({ error: '' }));
  }
}

export function* watchCrsDashboardGetFeatureCodesData() {
  yield takeEvery(GET_CRS_FEATURE_CATEGORY_CODES, function* ({ payload }) {
    yield fork(getFeatureCategoryCodesDataWorker, payload);
  });
}
